import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PackageSearch } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import { InventoryTable } from './sections/InventoryTable';
import { PriorityGroupTable } from './sections/PriorityGroupTable';
import { BuyerInventoryToolbar } from './sections/BuyerInventoryToolbar';
import {
  type BranchSelection,
  ALL_BRANCHES,
  availableBranchSelection,
  filterForBranchSelection,
  preferencesForBranchSelection,
  selectionFromPreferences,
} from '~/pages/buyer-inventory/lib/branchSelection';
import {
  useAssignedLocations,
  useAssignedLocationsWithInventory,
} from '~/pages/buyer-inventory/lib/useAssignedLocations';
import { useInventorySavedViews } from '~/pages/buyer-inventory/lib/useInventorySavedViews';
import { buildFiltersInput } from '~/pages/buyer-inventory/lib/buyerInventoryFilterStorage';
import { EmptyState } from '~/components/ui/EmptyState';
import { Button } from '~/components/ui';
import {
  useGetBuyerInventories,
  useGetBuyerInventoryRevealCounts,
  useGetUserPreferences,
  useUpdateUserPreferences,
} from '~/api';
import type { BuyerInventoryFiltersInput } from '~/graphql/graphql';
import { buildAndFilter } from '~/types';
import {
  isAllHiddenByLengthFilter,
  revealProbeParams,
} from './sections/inventoryLengthReveal';

function subtitleKey(selection: BranchSelection): string {
  if (selection.kind === 'all') return 'inventory.priority_queue_description';
  if (selection.kind === 'branch')
    return 'inventory.current_description_branch';
  return 'inventory.current_description';
}

export default function BuyerInventories() {
  const { t } = useTranslation('inventory');
  const location = useLocation();

  // includeInventoryLengths: true so a buyer whose entire stock is in inventory-only lengths still
  // counts as having inventory (and reaches the reveal CTA) instead of the hard "no inventory" state.
  const { data: baselineInventoryData, isLoading: baselineInventoryLoading } =
    useGetBuyerInventories(0, 1, { includeInventoryLengths: true });
  const noInventory =
    !baselineInventoryLoading &&
    (baselineInventoryData?.buyerInventory.paging.totalElements ?? 0) === 0;

  const inventoryViews = useInventorySavedViews();
  const panelFilter = useMemo(
    () => buildFiltersInput(inventoryViews.rows),
    [inventoryViews.rows]
  );
  const [searchFilter, setSearchFilter] = useState<BuyerInventoryFiltersInput>(
    {}
  );
  const [branchSelection, setBranchSelection] =
    useState<BranchSelection>(ALL_BRANCHES);
  const [includeInventoryLengths, setIncludeInventoryLengths] = useState(false);

  const { data: preferencesData } = useGetUserPreferences();
  const buyerLocations = useAssignedLocationsWithInventory();
  const [hasAppliedSavedBranch, setHasAppliedSavedBranch] = useState(false);
  const { mutate: updatePreferences } = useUpdateUserPreferences();
  const listedLocations = useMemo(() => buyerLocations ?? [], [buyerLocations]);
  const assignedLocations = useAssignedLocations();
  const activeBranchSelection = availableBranchSelection(
    branchSelection,
    listedLocations
  );

  const viewFilters = useMemo<BuyerInventoryFiltersInput | undefined>(() => {
    const branchFilter = filterForBranchSelection(
      activeBranchSelection,
      assignedLocations
    );
    const clauses: BuyerInventoryFiltersInput[] = [
      ...(Object.keys(panelFilter).length > 0 ? [panelFilter] : []),
      ...(searchFilter.or ? [searchFilter] : []),
      ...(Object.keys(branchFilter).length > 0 ? [branchFilter] : []),
    ];
    return clauses.length > 0 ? buildAndFilter(clauses) : undefined;
  }, [panelFilter, searchFilter, activeBranchSelection, assignedLocations]);

  // A dedicated counts-only probe returns both the visible count and the count with inventory-only
  // lengths revealed, so the reveal CTA is decided from one query without the table loads paying it.
  const revealProbeEnabled =
    !includeInventoryLengths && !noInventory && !baselineInventoryLoading;
  const { data: revealProbeData, isLoading: revealProbeLoading } =
    useGetBuyerInventoryRevealCounts(revealProbeParams(viewFilters), {
      enabled: revealProbeEnabled,
    });
  const revealProbePaging = revealProbeData?.buyerInventory.paging;

  const allHiddenByLengthFilter = isAllHiddenByLengthFilter({
    includeInventoryLengths,
    isLoading: revealProbeLoading,
    visibleCount: revealProbePaging?.totalElements ?? 0,
    totalIncludingHidden: revealProbePaging?.totalElementsIncludingHidden ?? 0,
  });

  const viewMode: 'queue' | 'flat' =
    activeBranchSelection.kind === 'all' ? 'queue' : 'flat';

  const updateFiltersWithSearchTerm = (term: string) => {
    const trimmed = term.trim();
    setSearchFilter(
      trimmed
        ? {
            or: [
              { productName: { contains: trimmed } },
              { productCode: { contains: trimmed } },
            ],
          }
        : {}
    );
  };

  if (preferencesData && buyerLocations && !hasAppliedSavedBranch) {
    setHasAppliedSavedBranch(true);
    const saved = preferencesData.userPreferences;
    setBranchSelection(
      selectionFromPreferences(
        {
          branchCode: saved.buyerInventorySelectedBranchCode,
          locationId: saved.buyerInventorySelectedLocationId,
        },
        buyerLocations
      )
    );
  }

  const handleBranchChange = (selection: BranchSelection) => {
    setBranchSelection(selection);
    updatePreferences({
      preferences: preferencesForBranchSelection(selection),
    });
  };

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="bg-neut-0 rounded-lg border border-neut-200 shadow-sm">
        <div className="px-6 py-4 border-b border-neut-200">
          <h1 className="text-2xl text-neut-900 font-semibold">
            {viewMode === 'queue'
              ? t('inventory.title')
              : t('inventory.current')}
          </h1>
          <p className="text-xs text-neut-500 mt-1">
            {t(subtitleKey(activeBranchSelection))}
          </p>
        </div>

        {noInventory ? (
          <div className="p-6">
            <EmptyState
              icon={<PackageSearch size={40} aria-hidden="true" />}
              title={t('inventory.empty_state_title')}
              message={t('inventory.empty_state_message')}
            />
            <div className="flex justify-center mt-4">
              <Link
                to="/settings"
                className="text-sm font-medium rounded-md bg-prim text-neut-0 hover:bg-prim-hover py-3 px-4"
              >
                {t('inventory.go_to_settings')}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <BuyerInventoryToolbar
              branchSelection={activeBranchSelection}
              locations={listedLocations}
              onBranchChange={handleBranchChange}
              onDebouncedSearchChange={updateFiltersWithSearchTerm}
              views={inventoryViews}
              includeInventoryLengths={includeInventoryLengths}
              onIncludeInventoryLengthsChange={setIncludeInventoryLengths}
            />

            {allHiddenByLengthFilter ? (
              <div className="p-6">
                <EmptyState
                  icon={<PackageSearch size={40} aria-hidden="true" />}
                  title={t('inventory.filtered_empty_title')}
                  message={t('inventory.filtered_empty_message')}
                />
                <div className="flex justify-center mt-4">
                  <Button
                    variant="primary"
                    onClick={() => setIncludeInventoryLengths(true)}
                  >
                    {t('inventory.filtered_empty_cta')}
                  </Button>
                </div>
              </div>
            ) : viewMode === 'queue' ? (
              <PriorityGroupTable
                t={t}
                filters={viewFilters}
                includeInventoryLengths={includeInventoryLengths}
              />
            ) : (
              <InventoryTable
                key={location.pathname}
                t={t}
                filters={viewFilters}
                includeInventoryLengths={includeInventoryLengths}
              />
            )}
          </>
        )}
      </div>
    </main>
  );
}
